'use es6';

import registerAllData from 'I18n/init/register/lazy/registerAllData';
import NotificationList from 'ReduxMessenger/components/NotificationList';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import UIApplication from 'UIComponents/app/UIApplication';
import CopilotProvider from 'copilot-toolkit/CopilotProvider';
import { renderWithNavCopilot } from 'copilot-toolkit/navCopilot/renderWithNavCopilot';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import isThenable from 'hs-promise-utils/isThenable';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import WindowDimensionsProvider from 'marketplace-ui-common/components/providers/WindowDimensionsProvider';
import MediaQueryStylesProvider from 'marketplace-ui-common/layout/components/MediaQueryStylesProvider';
import HSAnalyticsProvider from 'marketplace-ui-common/tracking/components/HSAnalyticsProvider';
import { UsageTrackerProvider } from 'marketplace-ui-tracking';
import { Provider } from 'react-redux';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { Router } from 'react-router-dom';
import { startApp } from './actions/AppActions';
import Routes from './components/Routes';
import { AppContextProvider } from './context/AppContext';
import I18nContext from './context/I18nContext';
import { setApiClient } from './utils/apiClient';
import configureStore from './utils/configureStore';
import { debug, logDebugging, setDebug, setDebugGate } from './utils/env';
import { createHistory } from './utils/history';

// Imports info on what base locales we support in I18n
// Usually used in Language/Locale selects
import 'I18n/init/data/baseLocales';
// Imports info on what extra locales we support in I18n
// (ie languages we support for our user's users)
// Usually used in Language/Locale selects
import 'I18n/init/data/publicLocales';
// Imports all currency data
// Needed if you render currency anywhere
import 'I18n/init/data/currency';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const dataFetchingClient = new DataFetchingClient();
export default class App {
  constructor({
    apiClient,
    appName,
    rhumbConfig,
    rootUrl,
    usageTracker,
    ApplicationComponent = UIApplication,
    RoutesComponent = Routes,
    blockingActions = [],
    isAdminPreview = false,
    isPublic = false,
    I18nProvider
  }) {
    this.setDebug = (key, ...args) => setDebug(key, ...args);
    this.setDebugGate = (gate, ...args) => setDebugGate(gate, ...args);
    setApiClient(apiClient);
    this.ApplicationComponent = ApplicationComponent;
    this.appName = appName;
    this.blockingActions = blockingActions;
    this.isAdminPreview = isAdminPreview;
    this.isPublic = isPublic;
    this.rhumbConfig = rhumbConfig;
    this.rootUrl = rootUrl;
    this.I18nProvider = I18nProvider;
    this.RoutesComponent = RoutesComponent;
    this.usageTracker = usageTracker;
  }
  start() {
    if (debug()) {
      window.appInstance = this;
      logDebugging();
    }
    const storeReady = this.startRedux();
    const langAvailable = Promise.all([this.I18nProvider.register(lang), registerAllData(this.I18nProvider)]);
    storeReady.then(() => {
      if (!(this.isPublic || this.isAdminPreview)) {
        const {
          user
        } = this.store.getState().auth;
        this.I18nProvider.setLocale({
          locale: user.locale,
          langEnabled: user.lang_enabled
        });
      }
      return langAvailable.then(() => {
        this.store.dispatch(startApp());
        this.startRouter(this.I18nProvider);
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
    return undefined;
  }
  startRedux() {
    this.store = configureStore();
    const promises = [];
    this.blockingActions.forEach(actionCall => {
      const maybePromise = this.store.dispatch(actionCall());
      if (isThenable(maybePromise)) {
        promises.push(maybePromise);
      }
    });
    return Promise.all(promises);
  }
  startRouter(i18nProvider) {
    const {
      ApplicationComponent,
      RoutesComponent,
      rootUrl
    } = this;
    this.history = createHistory(rootUrl);
    renderWithNavCopilot( /*#__PURE__*/_jsx(CopilotProvider, {
      children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
        client: dataFetchingClient,
        children: /*#__PURE__*/_jsx(Provider, {
          store: this.store,
          children: /*#__PURE__*/_jsx(RhumbProvider, {
            config: this.rhumbConfig,
            history: historyAdapter(this.history),
            children: /*#__PURE__*/_jsx(I18nContext.Provider, {
              value: i18nProvider,
              children: /*#__PURE__*/_jsx(HSAnalyticsProvider, {
                children: /*#__PURE__*/_jsx(MediaQueryStylesProvider, {
                  children: /*#__PURE__*/_jsx(WindowDimensionsProvider, {
                    children: /*#__PURE__*/_jsx(AppContextProvider, {
                      isAdminPreview: this.isAdminPreview,
                      isPublic: this.isPublic,
                      children: /*#__PURE__*/_jsx(UsageTrackerProvider, {
                        tracker: this.usageTracker,
                        children: /*#__PURE__*/_jsx(Router, {
                          history: this.history,
                          children: /*#__PURE__*/_jsxs(ApplicationComponent, {
                            name: this.appName,
                            children: [/*#__PURE__*/_jsx(NotificationList, {}), /*#__PURE__*/_jsx(UIFloatingAlertList, {}), /*#__PURE__*/_jsx(RoutesComponent, {})]
                          })
                        })
                      })
                    })
                  })
                })
              })
            })
          })
        })
      })
    }), {
      layoutType: 'panel-with-scroll'
    });
  }

  // Debug methods
}